import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { reviewsData } from '../../../mocks/reviews-detail';
import { consumerReviews } from '../../../mocks/consumer-reviews';
import ConsumerReviews from './components/ConsumerReviews';
import PageMeta from '../../../components/base/PageMeta';
import JsonLd from '../../../components/base/JsonLd';

export default function ReviewDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [signUpEmail, setSignUpEmail] = useState('');
  const [signUpPassword, setSignUpPassword] = useState('');
  const [signUpDone, setSignUpDone] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  const currentLang = i18n.language === 'vi' ? 'VI' : 'EN';
  const review = reviewsData.find((r) => r.slug === slug);

  useEffect(() => {
    if (!review) navigate('/reviews');
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [review, navigate]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!review) return null;

  const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';
  const ratingColor = review.rating >= 9 ? 'from-emerald-500 to-teal-500' : review.rating >= 7 ? 'from-violet-500 to-fuchsia-500' : 'from-orange-400 to-amber-500';

  const pageSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Review',
        name: `${review.name} Review – ${review.tagline}`,
        url: `${siteUrl}/reviews/${review.slug}`,
        datePublished: '2026-03-21',
        dateModified: '2026-03-21',
        itemReviewed: {
          '@type': 'SoftwareApplication',
          name: review.name,
          applicationCategory: review.category,
          url: review.website,
        },
        reviewRating: {
          '@type': 'Rating',
          ratingValue: review.rating,
          bestRating: 10,
          worstRating: 0,
        },
        author: { '@type': 'Organization', name: '247digitrend', url: siteUrl },
        publisher: { '@type': 'Organization', name: '247digitrend', url: siteUrl },
        reviewBody: review.overview,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Reviews', item: `${siteUrl}/reviews` },
          { '@type': 'ListItem', position: 3, name: review.name, item: `${siteUrl}/reviews/${review.slug}` },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <PageMeta
        title={`${review.name} Review ${new Date().getFullYear()} – Is It Worth It? | 247digitrend`}
        description={`${review.overview.substring(0, 155)}...`}
        canonical={`${siteUrl}/reviews/${review.slug}`}
        keywords={`${review.name} review, ${review.category}, best digital tools 2026`}
        ogType="article"
      />
      <JsonLd data={pageSchema} />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />

      {/* ── SIGN UP MODAL ── */}
      {showSignUp && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => { setShowSignUp(false); setSignUpDone(false); }}>
          <div className="relative bg-white rounded-2xl w-full max-w-sm mx-4 p-8" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => { setShowSignUp(false); setSignUpDone(false); }} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer">
              <i className="ri-close-line text-gray-600 text-base"></i>
            </button>
            {signUpDone ? (
              <div className="text-center py-4">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full bg-emerald-100">
                  <i className="ri-check-line text-emerald-600 text-2xl"></i>
                </div>
                <h3 className="text-lg font-black text-gray-900 mb-2">Registration Successful!</h3>
                <p className="text-sm text-gray-500">Welcome to 247compare.</p>
                <button onClick={() => { setShowSignUp(false); setSignUpDone(false); }} className="mt-6 w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold py-3 rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap">Close</button>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-pink-500">
                    <i className="ri-bar-chart-grouped-line text-white text-base"></i>
                  </div>
                  <h3 className="text-xl font-black text-gray-900 mb-1">Create Account</h3>
                  <p className="text-xs text-gray-500">Join 50K+ users of 247compare</p>
                </div>
                <form onSubmit={(e) => { e.preventDefault(); setSignUpDone(true); }} className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
                    <input type="email" required value={signUpEmail} onChange={(e) => setSignUpEmail(e.target.value)} placeholder="your@email.com" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-violet-400 transition-colors" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Password</label>
                    <input type="password" required value={signUpPassword} onChange={(e) => setSignUpPassword(e.target.value)} placeholder="At least 8 characters" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-violet-400 transition-colors" />
                  </div>
                  <button type="submit" className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold py-3 rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap mt-2">Sign Up Now</button>
                </form>
                <p className="text-xs text-center text-gray-400 mt-4">By signing up, you agree to our <a href="#" className="text-violet-600 underline">Terms</a>.</p>
              </>
            )}
          </div>
        </div>
      )}

      {/* HEADER */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-sm border-b border-gray-100' : 'bg-white'}`}>
        <div className="mx-auto px-8 py-2 flex items-center justify-between">
          <a href="/" className="flex items-center cursor-pointer">
            <img src="https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/7544398a-500f-41a6-81c3-aa7efbf1c0d2_logo-247.png?v=04135332188f9bc8b00780c66cd5837d" alt="247digitrend" className="h-36 w-auto object-contain" />
          </a>
          <nav className="hidden md:flex items-center gap-8">
            <a href="/" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Home</a>
            <a href="/categories" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Categories</a>
            <a href="/reviews" className="text-[19px] font-semibold text-violet-600 cursor-pointer">Reviews</a>
            <a href="/comparisons" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Comparisons</a>
            <a href="/blog" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Blog</a>
          </nav>
          <div className="flex items-center gap-3">
            <a href="/reviews" className="text-sm font-medium text-gray-600 hover:text-violet-600 transition-colors cursor-pointer flex items-center gap-1.5 whitespace-nowrap">
              <i className="ri-arrow-left-line text-base"></i> All Reviews
            </a>
            <div className="relative" ref={langRef}>
              <button onClick={() => setLangOpen(!langOpen)} className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 transition-colors rounded-full px-3 py-1.5 cursor-pointer">
                <i className="ri-translate-2 text-xs text-gray-500"></i>
                <span className="text-xs font-semibold text-gray-700">{currentLang}</span>
                <i className={`ri-arrow-down-s-line text-xs text-gray-400 transition-transform ${langOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl border border-gray-100 overflow-hidden w-32" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
                  {(['VI', 'EN'] as const).map((lang) => (
                    <button key={lang} onClick={() => { i18n.changeLanguage(lang.toLowerCase()); setLangOpen(false); }} className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold transition-colors cursor-pointer ${currentLang === lang ? 'bg-violet-50 text-violet-700' : 'text-gray-600 hover:bg-gray-50'}`}>
                      <span>{lang === 'VI' ? '🇻🇳' : '🇺🇸'}</span>
                      <span>{lang === 'VI' ? 'Tiếng Việt' : 'English'}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => setShowSignUp(true)} className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold px-5 py-2 rounded-full whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity">
              Sign Up
            </button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="pt-36 pb-10" style={{ background: 'linear-gradient(160deg, #faf5ff 0%, #fdf2ff 50%, #fff0fb 100%)' }}>
        <div className="max-w-6xl mx-auto px-8">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-6">
            <a href="/" className="hover:text-violet-600 transition-colors cursor-pointer">Home</a>
            <i className="ri-arrow-right-s-line"></i>
            <a href="/reviews" className="hover:text-violet-600 transition-colors cursor-pointer">Reviews</a>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-600 font-medium">{review.name}</span>
          </div>

          <div className="flex flex-col md:flex-row md:items-start gap-8">
            <div className="flex-1">
              <span className={`inline-flex items-center text-xs font-semibold px-3 py-1 rounded-full mb-3 ${review.categoryColor}`}>
                {review.category}
              </span>
              <h1 className="text-5xl font-black text-gray-900 mb-3 tracking-tight">{review.name}</h1>
              <p className="text-lg text-gray-500 leading-relaxed mb-5 max-w-2xl">{review.tagline}</p>
              <div className="flex flex-wrap items-center gap-3">
                <a href={review.website} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold px-5 py-2.5 rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap">
                  <i className="ri-external-link-line"></i> Visit {review.name}
                </a>
                <span className="text-xs text-gray-400 flex items-center gap-1.5">
                  <i className="ri-shield-check-line text-violet-400"></i> Independently reviewed by 247digitrend
                </span>
              </div>
            </div>

            {/* Rating Card */}
            <div className="md:w-52 flex-shrink-0">
              <div className="bg-white rounded-2xl p-6 text-center border border-gray-100" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Overall Score</p>
                <div className={`text-6xl font-black bg-gradient-to-br ${ratingColor} text-transparent bg-clip-text mb-1`}>{review.rating}</div>
                <p className="text-xs text-gray-400 mb-4">out of 10</p>
                <div className="flex justify-center gap-0.5 mb-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className={`text-base ${i < Math.round(review.rating / 2) ? 'ri-star-fill text-amber-400' : 'ri-star-line text-gray-200'}`}></i>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  {review.rating >= 9.5 ? 'Outstanding' : review.rating >= 8.5 ? 'Excellent' : review.rating >= 7.5 ? 'Very Good' : review.rating >= 6 ? 'Good' : 'Average'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MAIN CONTENT */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-8">
          <div className="flex flex-col lg:flex-row gap-10">

            {/* LEFT: Main content */}
            <div className="flex-1 min-w-0 space-y-10">

              {/* Screenshot */}
              <div className="rounded-2xl overflow-hidden border border-gray-100 w-full h-72">
                <img src={review.image} alt={review.name} className="w-full h-full object-cover object-top" />
              </div>

              {/* Overview */}
              <div>
                <h2 className="text-xl font-black text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-file-text-line text-violet-500"></i> Overview
                </h2>
                <p className="text-sm text-gray-600 leading-relaxed">{review.overview}</p>
              </div>

              {/* Key Features */}
              <div>
                <h2 className="text-xl font-black text-gray-900 mb-5 flex items-center gap-2">
                  <i className="ri-star-line text-violet-500"></i> Key Features
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {review.keyFeatures.map((feat, i) => (
                    <div key={i} className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl border border-gray-100">
                      <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500">
                        <i className={`${feat.icon} text-white text-base`}></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-gray-900 mb-1">{feat.title}</h3>
                        <p className="text-xs text-gray-500 leading-relaxed">{feat.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pros & Cons */}
              <div>
                <h2 className="text-xl font-black text-gray-900 mb-5 flex items-center gap-2">
                  <i className="ri-scales-3-line text-violet-500"></i> Pros &amp; Cons
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="bg-emerald-50 rounded-2xl p-5 border border-emerald-100">
                    <h3 className="text-sm font-black text-emerald-700 mb-4 flex items-center gap-2">
                      <i className="ri-thumb-up-fill text-emerald-500"></i> Pros
                    </h3>
                    <ul className="space-y-2.5">
                      {review.pros.map((pro, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-fill text-emerald-500 text-sm mt-0.5 flex-shrink-0"></i>
                          <span className="text-sm text-gray-700 leading-tight">{pro}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-red-50 rounded-2xl p-5 border border-red-100">
                    <h3 className="text-sm font-black text-red-600 mb-4 flex items-center gap-2">
                      <i className="ri-thumb-down-fill text-red-400"></i> Cons
                    </h3>
                    <ul className="space-y-2.5">
                      {review.cons.map((con, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <i className="ri-close-circle-fill text-red-400 text-sm mt-0.5 flex-shrink-0"></i>
                          <span className="text-sm text-gray-700 leading-tight">{con}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Pricing */}
              <div>
                <h2 className="text-xl font-black text-gray-900 mb-5 flex items-center gap-2">
                  <i className="ri-price-tag-3-line text-violet-500"></i> Pricing Plans
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {review.pricing.map((plan, i) => (
                    <div key={i} className={`rounded-2xl p-5 border ${plan.highlight ? 'border-violet-300 bg-violet-50' : 'border-gray-100 bg-white'}`}
                      style={plan.highlight ? { boxShadow: '0 4px 20px rgba(124,58,237,0.12)' } : { boxShadow: '0 1px 6px rgba(0,0,0,0.05)' }}>
                      {plan.highlight && (
                        <div className="inline-flex items-center gap-1 bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white text-xs font-bold px-2.5 py-0.5 rounded-full mb-3">
                          <i className="ri-fire-line text-xs"></i> Most Popular
                        </div>
                      )}
                      <h3 className="text-sm font-black text-gray-900 mb-1">{plan.plan}</h3>
                      <div className="flex items-end gap-0.5 mb-4">
                        <span className="text-3xl font-black text-gray-900">{plan.price}</span>
                        <span className="text-xs text-gray-400 mb-1 ml-0.5">{plan.period}</span>
                      </div>
                      <ul className="space-y-2">
                        {plan.features.map((f, fi) => (
                          <li key={fi} className="flex items-center gap-2 text-xs text-gray-600">
                            <i className="ri-check-line text-emerald-500 flex-shrink-0"></i> {f}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

              {/* Consumer Reviews */}
              {(() => {
                const trustpilot = consumerReviews.find((r) => r.slug === review.slug);
                return trustpilot ? <ConsumerReviews data={trustpilot} /> : null;
              })()}

              {/* Final Verdict */}
              <div className="rounded-2xl p-7 border border-violet-200" style={{ background: 'linear-gradient(135deg, #faf5ff, #fdf2ff)' }}>
                <h2 className="text-xl font-black text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-award-fill text-violet-500"></i> Final Verdict
                </h2>
                <p className="text-sm text-gray-700 leading-relaxed">{review.verdict}</p>
              </div>
            </div>

            {/* RIGHT: Sticky sidebar */}
            <div className="lg:w-72 flex-shrink-0 space-y-5">
              <div className="sticky top-36 space-y-5">

                {/* Rating Breakdown */}
                <div className="bg-white rounded-2xl p-5 border border-gray-100" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                  <h3 className="text-sm font-black text-gray-900 mb-4">Rating Breakdown</h3>
                  <div className="space-y-3">
                    {review.ratingBreakdown.map((item, i) => (
                      <div key={i}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-gray-600">{item.label}</span>
                          <span className="text-xs font-black text-gray-800">{item.score}</span>
                        </div>
                        <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full bg-gradient-to-r ${ratingColor}`}
                            style={{ width: `${item.score * 10}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Best For */}
                <div className="bg-white rounded-2xl p-5 border border-gray-100" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                  <h3 className="text-sm font-black text-gray-900 mb-3 flex items-center gap-2">
                    <i className="ri-user-heart-line text-violet-500"></i> Best For
                  </h3>
                  <p className="text-xs text-gray-600 leading-relaxed">{review.bestFor}</p>
                </div>

                {/* Quick CTA */}
                <div className="rounded-2xl p-5 text-center" style={{ background: 'linear-gradient(135deg, #7c3aed, #c026d3)' }}>
                  <p className="text-xs text-white/80 mb-3 font-medium">Ready to try {review.name}?</p>
                  <a href={review.website} target="_blank" rel="noopener noreferrer"
                    className="block w-full bg-white text-violet-700 text-sm font-bold py-2.5 rounded-full cursor-pointer hover:bg-violet-50 transition-colors whitespace-nowrap">
                    Visit Official Site
                  </a>
                </div>

                {/* Explore more */}
                <div className="bg-white rounded-2xl p-5 border border-gray-100" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                  <h3 className="text-sm font-black text-gray-900 mb-3">Explore More</h3>
                  <div className="space-y-2">
                    <a href="/reviews" className="flex items-center gap-2 text-xs font-medium text-gray-600 hover:text-violet-600 transition-colors cursor-pointer">
                      <i className="ri-star-line text-violet-400"></i> All Reviews
                    </a>
                    <a href="/comparisons" className="flex items-center gap-2 text-xs font-medium text-gray-600 hover:text-violet-600 transition-colors cursor-pointer">
                      <i className="ri-scales-3-line text-violet-400"></i> Tool Comparisons
                    </a>
                    <a href="/categories" className="flex items-center gap-2 text-xs font-medium text-gray-600 hover:text-violet-600 transition-colors cursor-pointer">
                      <i className="ri-grid-line text-violet-400"></i> Browse Categories
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ background: '#f9f5ff' }} className="pt-14 pb-6 mt-10">
        <div className="max-w-6xl mx-auto px-8">
          <div className="grid md:grid-cols-12 gap-10 pb-10 border-b border-gray-200">
            <div className="md:col-span-4">
              <div className="flex items-center mb-4">
                <img src="https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/7544398a-500f-41a6-81c3-aa7efbf1c0d2_logo-247.png?v=04135332188f9bc8b00780c66cd5837d" alt="247digitrend" className="h-36 w-auto object-contain" />
              </div>
              <p className="text-sm text-gray-500 leading-relaxed">
                247digitrend, operated by 247 Digital Marketing Co., Ltd., provides unbiased reviews and comparisons of online tools and digital platforms.
              </p>
            </div>
            <div className="md:col-span-2 md:col-start-6">
              <h5 className="text-sm font-bold text-gray-800 mb-4">Resources</h5>
              {[
                { label: 'All Reviews', href: '/reviews' },
                { label: 'Comparisons', href: '/comparisons' },
                { label: 'Categories', href: '/categories' },
                { label: 'Blog', href: '/blog' },
              ].map((item, i) => (
                <a key={i} href={item.href} className="block text-sm text-gray-500 hover:text-violet-600 transition-colors mb-3 cursor-pointer">{item.label}</a>
              ))}
            </div>
            <div className="md:col-span-2">
              <h5 className="text-sm font-bold text-gray-800 mb-4">Company</h5>
              {[{ label: 'About Us', href: '/about' }, { label: 'Contact', href: '/contact' }, { label: 'Submit a Tool', href: '/submit-tool' }, { label: 'Advertise', href: '/advertise' }].map((item, i) => (
                <a key={i} href={item.href} className="block text-sm text-gray-500 hover:text-violet-600 transition-colors mb-3 cursor-pointer">{item.label}</a>
              ))}
            </div>
            <div className="md:col-span-2">
              <h5 className="text-sm font-bold text-gray-800 mb-4">Follow Us</h5>
              <div className="flex items-center gap-3">
                {['ri-twitter-x-line', 'ri-linkedin-line', 'ri-facebook-line', 'ri-instagram-line'].map((icon, i) => (
                  <a key={i} href="#" className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 hover:text-violet-600 hover:border-violet-300 transition-all cursor-pointer">
                    <i className={`${icon} text-base`}></i>
                  </a>
                ))}
              </div>
            </div>
          </div>
          <div className="pt-5 flex flex-col md:flex-row items-center justify-between gap-3">
            <p className="text-xs text-gray-400">© 2026 247digitrend. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <a href="#" className="text-xs text-gray-400 hover:text-violet-600 transition-colors cursor-pointer">Privacy Policy</a>
              <a href="#" className="text-xs text-gray-400 hover:text-violet-600 transition-colors cursor-pointer">Terms of Service</a>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-xs text-gray-400 hover:text-violet-600 transition-colors cursor-pointer">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
